import { useState, useEffect, useRef } from "react"
import styles from "./css_modules/SearchComponent.module.css"
import AsyncSelect from 'react-select/async'
import Tooltip from "@mui/material/Tooltip"
import { Checkbox, FormControlLabel } from "@mui/material"
import Grid from '@mui/material/Grid2';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { styled } from '@mui/material/styles';
import Summary from "./Summary"
import DisplaySearch from "./DisplaySearch"
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

const LOADING_STATE = <section className={styles["text-part"]}>
    <div className={styles["loader"]}></div>
    <p>Loading data, please wait...</p>
</section>

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': { width: 15 },
      '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(9px)' },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(12px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: '#1890ff',
          ...theme.applyStyles('dark', { backgroundColor: '#177ddc' }),
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(['width'], { duration: 200 }),
    },
    '& .MuiSwitch-track': {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: 'rgba(0,0,0,.25)',
      boxSizing: 'border-box',
      ...theme.applyStyles('dark', { backgroundColor: 'rgba(255,255,255,.35)' }),
    },
}));

const logicConceptTooltip = "For the search concept(s) field, the checkbox specify if the search strategy should be an OR value logic or an AND value logic."

const SearchComponent = () => {

    const [author, setAuthor] = useState([])
    const [work, setWork] = useState([])
    const [concepts, setConcepts] = useState([])
    const [authorList, setAuthorList] = useState([])
    const [workList, setWorkList] = useState([])
    const [conceptList, setConceptList] = useState([])
    const [topConcepts, setTopConcepts] = useState([])
    const [lang, setLang] = useState('en')
    const [checked, setChecked] = useState(false)
    const [subConcepts, setSubConcepts] = useState(false)
    const [searchResult, setSearchResult] = useState(
    <div className={styles["empty-state"]}>
        <p className={styles["empty-state-title"]}>No results yet</p>
        <p className={styles["empty-state-subtitle"]}>Select one or more filters and click Search.</p>
    </div>
)
    const [currentWorkLoading, setCurrentWorkLoading] = useState("")
    const [summary, setSummary] = useState([])
    const [stats, setStats] = useState(null)

    const controller = useRef(new AbortController())

    const MAX_OPTIONS = 100

    const filterList = async (input, listOptions) => {
        return listOptions.filter(e => e.label.toLowerCase().includes(input.toLowerCase())).slice(0, MAX_OPTIONS)
    }

    const authorSelect = <AsyncSelect key={`author-select-${authorList.length}`} className={styles["selection-input"]}
        placeholder="select an author"
        defaultOptions={authorList}
        loadOptions={(input) => filterList(input, authorList)}
        isMulti
        onChange={(e) => setAuthor(e || [])}
    />

    const workSelect = <AsyncSelect key={`work-select-${workList.length}`} className={styles["selection-input"]}
        placeholder="select a work"
        defaultOptions={workList}
        loadOptions={(input) => filterList(input, workList)}
        isMulti
        onChange={(e) => setWork(e || [])}
    />

    const conceptsSelect = <AsyncSelect key={`concepts-select-${topConcepts.length}`} className={styles["selection-input"]}
        placeholder="select one or more concepts"
        defaultOptions={topConcepts}
        loadOptions={(input) => filterList(input, conceptList)}
        isMulti
        onChange={(e) => setConcepts(e || [])}
    />

    const changeLang = (e) => {
        getConcepts(e.target.value).then(setConceptList)
        getTopConcepts(e.target.value).then(setTopConcepts)
        setLang(e.target.value)
    }

    const sendRequestedForm = async () => {
        if(author.length === 0 && work.length === 0 && concepts.length === 0){
            console.log("Avoiding overloading: Search cancel due to no input...")
            return
        }

        setSearchResult(LOADING_STATE)
        setStats([])
        setSummary([])

        if (controller.current) {
            controller.current.abort("Canceling Fetch: Work has changed...")
        }
        controller.current = new AbortController()
        const signal = controller.current.signal

        const searchObject = JSON.stringify({
            author: author.map(e => e.value),
            work: work.map(e => e.value),
            concepts: concepts.map(e => {return{uri: e.value, type: e.type}}),
            checked: checked,
            subConcepts: subConcepts
        })
        let time1 = performance.now()
        const data_retreive = await fetch(`${process.env.REACT_APP_BACKEND_URL}customSearch`,
            {
                method: "POST",
                mode: "cors",
                headers: { "Content-Type": "application/json" },
                body: searchObject,
                signal
            }
        ).then(response => response.json()).catch(e => {console.log(e)})
        let time2 = performance.now()

        const displaySearch = []
        if(Object.keys(data_retreive.tree).length === 0){
            displaySearch.push(<div className={styles["box-result"]}><p>No result for this custom filter</p></div>)
            setSummary([])
        } else {
            setStats(<section className={styles["selected-book-metadata"]}>
                <div className={styles["block-stat"]}>
                    <h4>Results</h4>
                    <p>Number of Work: {data_retreive.tree.length}</p>
                    <p>Request time: {((time2 - time1) / 1000).toFixed(2)} s</p>
                </div>
                <div className={styles["block-stat"]}>
                    <h4>Export SPARQL Result:</h4>
                    <div className={styles["btn-export-multiple"]}>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}download-custom-search-json?sparql=${encodeURIComponent(data_retreive.sparql)}`} className={styles["btn-download"]} download target="_blank" rel="noreferrer">JSON</a>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}download-custom-search-csv?sparql=${encodeURIComponent(data_retreive.sparql)}`} className={styles["btn-download"]} download target="_blank" rel="noreferrer">CSV</a>
                    </div>
                </div>
                {currentWorkLoading}
            </section>)
            setSummary(<div key="main" className={styles["ul-toc"]}>
                <h2>Table of contents</h2>
                <ul>{data_retreive.tree !== null ? data_retreive.tree.map(node => 
                    <SimpleTreeView key={node.uri}>
                        <Summary key={node.uri} node={node} currentBook={null} setChange={null} setCurrentBook={null} />
                    </SimpleTreeView>
                ) : ''}</ul>
            </div>)
            for(const node of data_retreive.tree){
                displaySearch.push(<DisplaySearch node={node} controller={controller}/>)
            }
        }
        
        setCurrentWorkLoading("")
        setSearchResult(displaySearch)
    }

    const getConcepts = async (language="en") => {
        const data_concepts = await fetch(`${process.env.REACT_APP_BACKEND_URL}getTheso?lang=${language}`
        ).then(response => response.json())
            .catch(e => { console.log(e) })
        return data_concepts
    }

    const getTopConcepts = async (language="en") => {
        const data_concepts = await fetch(`${process.env.REACT_APP_BACKEND_URL}getTopConcepts?lang=${language}`
        ).then(response => response.json())
            .catch(e => { console.log(e); return [] })
        return data_concepts
    }

    useEffect(() => {
        const getAuthors = async () => {
            const data_author = await fetch(`${process.env.REACT_APP_BACKEND_URL}getWorks`
            ).then(response => response.json()).catch(e => { console.log(e); return [] })
            const seen = new Set()
            const author_response = []
            for (const author of data_author) {
                if (!seen.has(author.author)) {
                    seen.add(author.author)
                    author_response.push({ value: author.author, label: author.author })
                }
            }
            return author_response
        }
    
        const getWorks = async () => {
            const work_response = []
            const data_works = await fetch(`${process.env.REACT_APP_BACKEND_URL}getWorks`
            ).then(response => response.json()).catch(e => { console.log(e); return [] })
            for (const work of data_works) {
                work_response.push({ value: work.uri, label: work.language ? `${work.title} (${work.language})` : work.title, author: work.author })
            }
            return work_response
        }

        getAuthors().then(setAuthorList)
        getWorks().then(setWorkList)
        getConcepts().then(setConceptList)
        getTopConcepts().then(setTopConcepts)
    }, [])

    return <div className={styles["box-content"]}>
        <section className={styles["section-form"]}>
            <div className={styles["block-input"]}>
                <p className={styles["filter-by-label"]}>Filter by:</p>
                <div className={styles["search-input"]}>
                    <span className={styles["field-label"]}>Author</span>
                    {authorSelect}
                </div>
                <div className={styles["search-input"]}>
                    <span className={styles["field-label"]}>Work</span>
                    {workSelect}
                </div>
                <div className={styles["search-input-border"]}>
    <div className={styles["search-concept"]}>
        <span className={styles["field-label"]}>Concepts</span>
        {conceptsSelect}
        <FormControl size="small" sx={{ m: 1, minWidth: 70 }}>
                            <Select
                                size="small"
                                value={lang}
                                onChange={changeLang}
                                sx={{ '& .MuiSelect-select': { textAlign: 'center' } }}>
                                <MenuItem value="en" sx={{ justifyContent: 'center' }}>EN</MenuItem>
                                <MenuItem value="fr" sx={{ justifyContent: 'center' }}>FR</MenuItem>
                                <MenuItem value="it" sx={{ justifyContent: 'center' }}>IT</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className={styles["logic-selector"]}>
                        <Tooltip title={logicConceptTooltip}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>OR</Typography>
                                <AntSwitch checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                                <Typography>AND</Typography>
                            </Stack>
                        </Tooltip>
                        <FormControlLabel
                            className={styles["concept-checkbox"]}
                            control={<Checkbox color="secondary" onChange={e => setSubConcepts(!subConcepts)}/>}
                            label="Include sub-concepts" />
                    </div>
                </div>
                <div className={styles["search-btn-section"]}>
                    <button className={styles["btn-submit-search"]} onClick={sendRequestedForm}>Search</button>
                </div>
            </div>
        </section>
        <Grid container spacing={2}>
            <Grid size={12}>{stats}</Grid>
            <Grid size={2}>{summary}</Grid>
            <Grid size={10}>{searchResult}</Grid>
        </Grid>
    </div>
}

export default SearchComponent;